/**
 * Reusable searchable artist picker for admin forms.
 *
 * Hits `/api/admin/users/search?q=…` (debounced 200 ms). Renders a
 * dropdown of live matches with artist_name / name / email / user_id
 * so admins never have to memorize or copy-paste raw user_ids.
 *
 * Props:
 *   value        — current user_id (string) or ""
 *   onChange({user_id, artist_name}) — fires when a user is selected
 *   testIdPrefix — prefix for data-testids
 *   label        — field label
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { optimizeImage } from "@/lib/mediaUrls";

export const ArtistPicker = ({
  value,
  onChange,
  testIdPrefix = "artist-picker",
  label = "Artist",
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  // If a user_id is set externally but we don't have their name, resolve.
  useEffect(() => {
    if (!value || selected?.user_id === value) return;
    // Prefer public artist endpoint (works for any admin/non-admin lookup).
    (async () => {
      try {
        const r = await api.get(`/artist/${value}`);
        setSelected({
          user_id: value,
          artist_name: r.data?.artist_name || r.data?.name || value,
        });
      } catch {
        setSelected({ user_id: value, artist_name: value });
      }
    })();
  }, [value, selected?.user_id]);

  const runSearch = useCallback((q) => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    setBusy(true);
    api.get("/admin/users/search", { params: { q, limit: 15 } })
      .then((r) => setResults(r.data?.results || []))
      .catch(() => setResults([]))
      .finally(() => setBusy(false));
  }, []);

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(v.trim()), 200);
  };

  const pick = (u) => {
    setSelected({ user_id: u.user_id, artist_name: u.artist_name || u.name || u.user_id });
    onChange({ user_id: u.user_id, artist_name: u.artist_name || u.name });
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const clear = () => {
    setSelected(null);
    onChange({ user_id: "", artist_name: "" });
    setQuery("");
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapRef} className="relative" data-testid={testIdPrefix}>
      <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
        {label} <span className="text-rose-400">*</span>
      </label>
      {selected ? (
        <div
          className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/40"
          data-testid={`${testIdPrefix}-selected`}
        >
          <div className="min-w-0 flex-1">
            <p className="text-white text-sm font-black truncate">{selected.artist_name}</p>
            <p className="text-[10px] text-emerald-300 font-mono truncate">{selected.user_id}</p>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={clear}
              className="text-xs text-rose-300 hover:text-rose-100 font-black shrink-0"
              data-testid={`${testIdPrefix}-clear`}
            >
              Change
            </button>
          )}
        </div>
      ) : (
        <>
          <Input
            value={query}
            onChange={onInput}
            onFocus={() => setOpen(true)}
            placeholder="Search by artist name, email, or handle…"
            disabled={disabled}
            className="bg-white/5 border-white/10 text-white"
            data-testid={`${testIdPrefix}-input`}
          />
          {open && (results.length > 0 || busy || query.length >= 2) && (
            <div
              className="absolute z-50 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-md border border-white/10 bg-[#0f0f14] shadow-xl"
              data-testid={`${testIdPrefix}-results`}
            >
              {busy && (
                <p className="text-xs text-gray-500 px-3 py-2">Searching…</p>
              )}
              {!busy && results.length === 0 && query.length >= 2 && (
                <p className="text-xs text-gray-500 px-3 py-2" data-testid={`${testIdPrefix}-no-results`}>
                  No artists match &quot;{query}&quot;
                </p>
              )}
              {!busy && results.map((u) => (
                <button
                  key={u.user_id}
                  type="button"
                  onClick={() => pick(u)}
                  className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 flex items-center gap-2"
                  data-testid={`${testIdPrefix}-result-${u.user_id}`}
                >
                  {u.profile_photo ? (
                    <img src={optimizeImage(u.profile_photo, 'avatar')} alt="" className="w-8 h-8 rounded-full object-cover border border-white/10" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-xs">🎤</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-black truncate">{u.artist_name || u.name || u.user_id}</p>
                    <p className="text-[10px] text-gray-500 truncate">
                      {u.email || u.user_id}
                      {typeof u.track_count === "number" && ` · ${u.track_count} track${u.track_count === 1 ? "" : "s"}`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArtistPicker;
